import { memo } from 'react';

import { ArrowDownIcon } from '@air/design-icons';

import { getBrandedButtonStyle } from '~/components/v2/utils/filters/getContrastColor';

export type DesignPreviewTitleRowProps = {
  title: string;
  /** Resolved hex for the title text and the branded download button. */
  buttonColor: string;
  /** Optional CSS `font-family` applied to the title so the picked headline font is reflected live. */
  fontFamily?: string;
  /**
   * The picked headline font variant's weight and style, so the preview shows the same face the
   * public page will render. Absent (no font, or a font with no variant) leaves the title's own
   * `font-bold` in charge, exactly as before — the same fallback contract the public page's
   * `--headline-font-weight` gives its headline surfaces.
   */
  fontWeight?: number;
  fontStyle?: 'italic' | 'normal';
};

/** The preview's title alongside the branded (download) action button. */
export const DesignPreviewTitleRow = memo(
  ({ title, buttonColor, fontFamily, fontWeight, fontStyle }: DesignPreviewTitleRowProps) => (
    <div className="flex items-center justify-between gap-2">
      <span
        className="min-w-0 truncate font-bold text-12 leading-4"
        data-testid="SHARE_DESIGN_TAB_PREVIEW_TITLE"
        style={{ color: buttonColor, fontFamily, fontWeight, fontStyle }}
      >
        {title}
      </span>
      <div
        className="flex shrink-0 items-center gap-1 rounded-4 px-2 py-1 font-semibold text-8 leading-[133%]"
        data-testid="SHARE_DESIGN_TAB_PREVIEW_DOWNLOAD_BUTTON"
        style={getBrandedButtonStyle(buttonColor)}
      >
        <ArrowDownIcon className="size-[9px] shrink-0" />
        Download
      </div>
    </div>
  ),
);

DesignPreviewTitleRow.displayName = 'DesignPreviewTitleRow';
